import * as React from 'react'

import { KonvaEventObject } from 'konva/lib/Node'
import { action, computed, makeObservable, observable } from 'mobx'
import { inject, MobXProviderContext, observer, Provider } from 'mobx-react'

import BaseMapView from '~/client/src/shared/components/BaseMapView/BaseMapView'
import BaseMapViewSetUpStore from '~/client/src/shared/components/SitemapHelpers/BaseMapViewSetUp.store'
import ICanvasImageCache from '~/client/src/shared/interfaces/ITextboxesCache'
import Delivery from '~/client/src/shared/models/Delivery'
import GlobeView from '~/client/src/shared/models/GlobeView'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import SharedDeliveriesStore from '~/client/src/shared/stores/domain/SharedDeliveries.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import CommonStore from '~/client/src/shared/stores/ui/Common.store'
import DeliverySitemapPinStore from '~/client/src/shared/stores/ui/DeliverySitemapPin.store'

import DeliveryWorkflowCard from '../../../../ExpandableWorkflowCard/components/DeliveryWorkflowCard'
import {
  buildDeliveryPinData,
  IDeliveryPinGroup,
} from '../../../../LogisticsMapView/deliveryPinPoints'
import {
  buildSharedPinPoints,
  ISharedPinGroup,
} from '../../../../LogisticsMapView/sharedPinPoints'
import { IWorkflowPinPoint } from '../../../../LogisticsMapView/workflowPinPoints'
import MapBoxViewerStore from '../../../../MapBoxViewer/MapBoxViewer.store'
import MapCardCarousel from '../../../../MapCardCarousel/MapCardCarousel'
import { MapControls } from '../../../../MapControls'
import KonvaSitemapItems from '../../../../SitemapHelpers/components/KonvaSitemapItems'
import MapViewItemBase from '../../../../SitemapHelpers/models/MapViewItemBase'
import DeliveriesMapViewStore from '../DeliveriesMapView.store'
import DeliveryMapLayers from './DeliveryMapLayers'
import KonvaDeliveryPins from './KonvaDeliveryPins'

interface IProps {
  sitemapId: string
  globeViewsStore?: GlobeViewsStore

  globe?: GlobeView
  textboxesCache: ICanvasImageCache

  mapStore: DeliveriesMapViewStore
  mapBoxViewerStore: MapBoxViewerStore

  state?: InitialState
  sitemapsStore?: SitemapsStore
  basemapsStore?: BasemapsStore
  sitemapItemsStore?: SitemapItemsStore
  locationAttributesStore?: LocationAttributesStore
  companiesStore?: CompaniesStore
  common?: CommonStore
  sharedDeliveriesStore?: SharedDeliveriesStore

  isMobileEnv?: boolean
}

const deliveryKey = (delivery: Delivery): string => delivery.id

@inject(
  'state',
  'sitemapsStore',
  'basemapsStore',
  'sitemapItemsStore',
  'locationAttributesStore',
  'companiesStore',
  'globeViewsStore',
  'common',
  'isMobileEnv',
  'sharedDeliveriesStore',
)
@observer
export default class MobileDeliveryMapView extends React.Component<IProps> {
  @observable private localContext = null
  @observable private localProps: IProps = null
  public static contextType = MobXProviderContext
  @observable private deliverySitemap: BaseMapView
  private readonly store: BaseMapViewSetUpStore = null
  private readonly sitemapPinStore: DeliverySitemapPinStore = null
  private selectedDeliveriesMap: { [attrId: string]: boolean } = {}
  private selectedMapboxDeliveriesMap: { [attrId: string]: string } = {}

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    this.store = new BaseMapViewSetUpStore(
      props.sitemapsStore,
      props.globeViewsStore,
      props.basemapsStore,
      props.sitemapItemsStore,
      props.locationAttributesStore,
    )
    this.sitemapPinStore = new DeliverySitemapPinStore(props.state)

    props.mapBoxViewerStore.setViewportFromAddress()
    props.mapBoxViewerStore.setDefaultMapMode()
    this.store.selectSitemapFromId(props.sitemapId)
    makeObservable(this)
  }

  public UNSAFE_componentWillMount(): void {
    this.localContext = this.context
  }

  public componentDidMount(): void {
    this.props.mapStore.setSelectedDeliveryId(
      this.props.mapStore.displayedSitemapDeliveries?.[0]?.id,
    )
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (this.props.sitemapId !== prevProps.sitemapId) {
      this.store.selectSitemapFromId(this.props.sitemapId)
    }
  }

  public render() {
    const { mapBoxViewerStore, state, globe, sitemapId, sitemapsStore } =
      this.props
    const { sitemapUrl } = this.store

    const sitemap = sitemapsStore.byId.get(sitemapId)
    return (
      <div className="relative general-map-container full-width full-height">
        <BaseMapView
          globe={globe}
          selectedSitemap={sitemap}
          isDeliveryView={true}
          sitemapUrl={sitemapUrl}
          isDraggable={true}
          mapBoxViewerStore={mapBoxViewerStore}
          projectAddress={state.projectAddress}
          ref={this.setDeliverySitemap}
          isMobileEnv={this.props.isMobileEnv}
        >
          {({ width, height }) => {
            if (!this.deliverySitemap) {
              return null
            }
            return (
              <Provider {...this.localContext}>
                {globe
                  ? this.renderGlobeViewChildren()
                  : this.renderSitemapChildren(width, height)}
              </Provider>
            )
          }}
        </BaseMapView>
        {this.renderDeliveryCards()}
      </div>
    )
  }

  private renderDeliveryCards(): JSX.Element {
    const {
      isGallerySwipingDisabled,
      displayedSitemapDeliveries,
      selectedDeliveryId,
      setSelectedDeliveryId,
      openDeliveryDetails,
    } = this.props.mapStore

    if (isGallerySwipingDisabled) {
      return null
    }

    return (
      <MapControls.AboveAttribution>
        <MapCardCarousel
          items={displayedSitemapDeliveries}
          selectedId={selectedDeliveryId}
          keyExtractor={deliveryKey}
          renderCard={delivery => (
            <DeliveryWorkflowCard
              delivery={delivery}
              onClick={openDeliveryDetails}
            />
          )}
          onSelectedChange={setSelectedDeliveryId}
        />
      </MapControls.AboveAttribution>
    )
  }

  private setDeliverySitemap = ref => {
    this.deliverySitemap = ref
  }

  private renderGlobeViewChildren = () => (
    <DeliveryMapLayers
      mapBoxViewerStore={this.localProps.mapBoxViewerStore}
      deliveryPoints={this.deliveryPinData.points}
      sharedPoints={this.sharedPinData.points}
      onDeliveryPinClick={this.selectDeliveriesByCoordKey}
      onSharedPinClick={this.selectSharedDeliveryByCoordKey}
    />
  )

  // Deliveries shared in from another project. They carry no local marker, so they group by
  // their own coordinate and open the same shared-delivery view the DOM markers opened.
  @computed
  private get sharedPinData(): {
    points: IWorkflowPinPoint[]
    groupsByCoordKey: Map<string, ISharedPinGroup>
  } {
    return buildSharedPinPoints([], this.localProps.sharedDeliveriesStore.list)
  }

  private selectSharedDeliveryByCoordKey = (coordKey: string) => {
    const delivery =
      this.sharedPinData.groupsByCoordKey.get(coordKey)?.deliveries[0]
    if (delivery) {
      this.localProps.common.displaySharedDeliveryView(delivery.id)
    }
  }

  @computed
  private get deliveryPinData(): {
    points: IWorkflowPinPoint[]
    groupsByCoordKey: Map<string, IDeliveryPinGroup>
  } {
    const {
      state: { user },
      mapStore: { selectedDeliveryId },
      mapBoxViewerStore,
    } = this.localProps
    return buildDeliveryPinData(
      mapBoxViewerStore.displayedGlobeViewItems,
      this.attrIdToMapboxDeliveriesMap,
      this.sitemapPinStore,
      user,
      deliveries =>
        deliveries.some(d => this.selectedDeliveriesMap[d.id]) ||
        deliveries.some(d => d.id === selectedDeliveryId),
    )
  }

  @action.bound
  private selectDeliveriesByCoordKey(coordKey: string) {
    const group = this.deliveryPinData.groupsByCoordKey.get(coordKey)
    if (group) this.onDeliveryMarkerClick(group.deliveries)
  }

  private renderSitemapChildren = (width: number, height: number) => {
    const {
      textboxesCache,
      companiesStore,
      state: { user },
      mapStore: { selectedDeliveryId },
    } = this.localProps

    return (
      <>
        <KonvaSitemapItems
          items={this.store.displayedSitemapItems || []}
          containerWidth={width}
          containerHeight={height}
          textboxesCache={textboxesCache}
        />
        <KonvaDeliveryPins
          items={this.sortedSitemapItems}
          containerWidth={width}
          containerHeight={height}
          deliveriesByItemId={this.attrIdToDeliveriesMap}
          pinStore={this.sitemapPinStore}
          user={user}
          companiesStore={companiesStore}
          isPinSelected={deliveries =>
            deliveries.some(d => d.id === selectedDeliveryId)
          }
          onPinClick={(deliveries, _item, event) =>
            this.onDeliveryPillTouch(deliveries, event)
          }
          onPinTouchEnd={(deliveries, _item, event) =>
            this.onDeliveryPillTouch(deliveries, event)
          }
        />
      </>
    )
  }

  @computed
  public get attrIdToDeliveriesMap(): { [attrId: string]: Delivery[] } {
    const { displayedSitemapDeliveries } = this.localProps.mapStore

    this.resetSelectedDeliveriesMap()

    return this.sortedSitemapItems
      .map(i => i.id)
      .reduce((acc, attrId) => {
        acc[attrId] = displayedSitemapDeliveries.filter(
          d => d.isRelatedAttr(attrId) && !this.selectedDeliveriesMap[d.id],
        )

        this.selectDeliveries(acc[attrId].map(d => d.id))
        return acc
      }, {})
  }

  @computed
  public get attrIdToMapboxDeliveriesMap(): { [attrId: string]: Delivery[] } {
    const { availableDeliveries } = this.localProps.mapStore
    this.resetMapboxSelectedDeliveriesMap()

    const displayedAttrsIds =
      this.localProps.mapBoxViewerStore.displayedGlobeViewItems.map(si => si.id)

    return displayedAttrsIds.reduce((acc, attrId) => {
      acc[attrId] = availableDeliveries.filter(
        d => d.isRelatedAttr(attrId) && !this.selectedMapboxDeliveriesMap[d.id],
      )

      this.selectMapboxDeliveries(
        acc[attrId].map(d => d.id),
        attrId,
      )
      return acc
    }, {})
  }

  @computed
  private get sortedSitemapItems(): MapViewItemBase[] {
    return this.sitemapPinStore
      .getItemsByPinLocationOption(this.store.displayedSitemapItems)
      .filter(item => !item.isDataLess)
  }

  private selectDeliveries = (deliveriesIds: string[]) => {
    deliveriesIds.forEach(id => {
      this.selectedDeliveriesMap[id] = true
    })
  }

  private selectMapboxDeliveries = (
    deliveriesIds: string[],
    attrId: string,
  ) => {
    deliveriesIds.forEach(id => {
      this.selectedMapboxDeliveriesMap[id] = attrId
    })
  }

  @action.bound
  private resetMapboxSelectedDeliveriesMap(): void {
    this.selectedMapboxDeliveriesMap = {}
  }

  @action.bound
  private resetSelectedDeliveriesMap(): void {
    this.selectedDeliveriesMap = {}
  }

  @action.bound
  // Bound to both click and touch: all it needs off the event is cancelBubble.
  private onDeliveryPillTouch(
    deliveries: Delivery[],
    event: KonvaEventObject<MouseEvent | TouchEvent>,
  ): void {
    const { isGallerySwipingDisabled, setSelectedDeliveryId } =
      this.props.mapStore

    if (!isGallerySwipingDisabled) {
      setSelectedDeliveryId(deliveries?.[0]?.id)
    }

    event.cancelBubble = true
  }

  @action.bound
  private onDeliveryMarkerClick(deliveries: Delivery[]): void {
    const { isGallerySwipingDisabled, setSelectedDeliveryId } =
      this.props.mapStore

    if (!isGallerySwipingDisabled) {
      setSelectedDeliveryId(deliveries?.[0]?.id)
    }
  }
}
